import { WithAuth0State, WithAuth0Props } from 'interfaces/auth.interface';
import { Component, ComponentType, createElement } from 'react';
import { getAuth0User, checkAuthStatus } from 'services/auth.service';
import { AUTH_EVENT } from 'constants/events';

export default function withAuth0<T extends WithAuth0Props>(
  WrappedComponent: ComponentType<T>
): ComponentType<Omit<T, keyof WithAuth0Props>> {
  class WithAuth extends Component<
    Omit<T, keyof WithAuth0Props>,
    WithAuth0State
  > {
    state: WithAuth0State = {
      isAuthenticated: false,
      auth0User: null,
      authenticating: true
    };

    async componentDidMount() {
      this.refreshAuthState();
      window.addEventListener(AUTH_EVENT.UPDATED, this.refreshAuthState);
    }

    componentWillUnmount(): void {
      window.removeEventListener(AUTH_EVENT.UPDATED, this.refreshAuthState);
    }

    refreshAuthState = async () => {
      try {
        this.setState({ authenticating: true });
        const auth0Check = await checkAuthStatus();
        if (auth0Check) {
          const auth0User = await getAuth0User();
          this.setState({
            isAuthenticated: true,
            auth0User,
            authenticating: false
          });
        } else {
          this.setState({ authenticating: false });
        }
      } catch (err) {
        this.setState({ authenticating: false });
      }
    };

    render() {
      const { isAuthenticated, auth0User, authenticating } = this.state;
      const authProps = {
        isAuthenticated,
        auth0User,
        authenticating
      } as WithAuth0Props;

      return createElement(WrappedComponent, {
        ...(this.props as T),
        ...authProps
      });
    }
  }

  WrappedComponent.displayName = WrappedComponent.name;
  return WithAuth;
}
